import { RundownToken } from '@/axios'
import { db } from '@/firebase'
import { getRundownEvent, rundownPlan } from '@/firestore'
import Sentry, { captureException } from '@/sentry'
import { isAxiosError } from 'axios'
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  type DocumentSnapshot,
  type FirestoreError,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { getDefaultStore } from 'jotai'
import { proxy, snapshot, useSnapshot, type Snapshot } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { queryClient } from '@/lib/react-query'
import { resetStore } from '@/lib/valtio/reset-store'
import { triggerToast } from '@/shared/ui/toast'
import { getErrorMessage } from '@/utils/errors'

import {
  CELL_COLLECTION,
  COLUMN_COLLECTION,
  CUE_COLLECTION,
  MENTION_COLLECTION,
  RUNDOWN_COLLECTION,
  RUNNER_COLLECTION,
} from '~shared/consts'
import { parseRundownSnapshot, type Rundown } from '~shared/rundown/rundown'
import { SUB_TIMER_RUN_COLLECTION } from '~shared/rundown/sub-timer-run'
import {
  mentionFromSnapshot,
  planFromSerialized,
  type DocumentSnapshot as SharedDocumentSnapshot,
  type Runner,
  eventFromSerialized,
} from '~shared/types'
import { parseSnapshot } from '~shared/utils'

import { mentionsQueryKeys } from '@/entities/mentions/api/hooks'
import { variablesAtom } from '@/entities/variables'
import { variablesQueryKeys } from '@/entities/variables/api/hooks'
import { eventAtom } from '@/store/event.store'
import { planAtom } from '@/store/plan.store'
import { runnerAtom } from '@/store/runner.store'

import { columnShareLinkActions } from '@/features/rundown/column/column-share-link'
import { resetCueOrderSave } from '@/features/rundown/cue/cue-dnd.actions'
import { cueDndActions } from '@/features/rundown/cue/cue-dnd.store'
import { pendingCueOrderActions } from '@/features/rundown/cue/cue-order-pending.store'
import { cueTimingActions } from '@/features/rundown/cue/cue-timing.store'
import { cueUiActions } from '@/features/rundown/cue/cue-ui.store'
import { subTimerRunsActions } from '@/features/rundown/cue/sub-timers/sub-timers.store'
import { cellsStoreActions } from '@/features/rundown/data/cells.store'
import { columnsStoreActions } from '@/features/rundown/data/columns.store'
import { cuesStoreActions } from '@/features/rundown/data/cues.store'
import { rundownStoreActions } from '@/features/rundown/data/rundown.store'

// =============== TYPES

export type RundownSessionStatus = 'idle' | 'loading' | 'ready' | 'error'

/**
 * Why the session failed.
 *
 * Modelled on the actual causes. `unknown` keeps the raw details for display.
 */
export type RundownSessionError =
  | { reason: 'not-found' }
  | { reason: 'archived' }
  | { reason: 'unauthorized' }
  | {
      reason: 'unknown'
      status: number | null
      message: string
      /**
       * The original error, kept for Sentry and <ErrorView/>.
       * Valtio does not proxy `Error` instances.
       */
      cause: Error
    }

/**
 * Everything the RundownRoute requires before the child pages render.
 *
 * Each flag flips on the first snapshot / response, even if that snapshot is
 * empty (a rundown could have zero cues, columns, or cells).
 */
type Loaded = {
  token: boolean
  plan: boolean
  rundown: boolean
  cues: boolean
  columns: boolean
  cells: boolean
}

type RundownSessionState = {
  rundownId: string | null
  status: RundownSessionStatus
  error: RundownSessionError | null
  loaded: Loaded
}

const initialState: RundownSessionState = {
  rundownId: null,
  status: 'idle',
  error: null,
  loaded: {
    token: false,
    plan: false,
    rundown: false,
    cues: false,
    columns: false,
    cells: false,
  },
}

// =============== STORE

/**
 * Lifecycle of the currently open rundown page.
 *
 * @remarks
 *   Read-only outside this module. Data itself lives in the domain stores
 *   (Jotai atoms today, valtio stores as they get migrated), this store only
 *   tracks whether the page is ready to render.
 */
const sessionStore = proxy<RundownSessionState>(deepClone(initialState))

/**
 * Per-session handle. Everything a session owns is closed over here, so an
 * overlapping start/stop (StrictMode, fast navigation) can never leak a
 * listener from one session into the next.
 */
type SessionHandle = {
  rundownId: string
  unsubscribers: Unsubscribe[]
  unsubscribeRunner: Unsubscribe | null
  runnerId: string | null
  eventId: string | null
  variableKeys: string[]
  stopped: boolean
  /** The out-of-date toast, shown at most once per session. */
  staleToast: { dismiss: () => void } | null
}

let activeSession: SessionHandle | null = null

const jotai = getDefaultStore()

// =============== PUBLIC API

/**
 * Start loading a rundown.
 *
 * Idempotent for the same `rundownId`; a different `rundownId` stops the
 * previous session first.
 *
 * Token request and all Firestore subscriptions start immediately and in
 * parallel. Nothing waits on the HTTP round trips to Cloud Functions.
 */
export function startRundownSession(rundownId: string): void {
  if (activeSession?.rundownId === rundownId) return
  if (activeSession) stopRundownSession()

  const session: SessionHandle = {
    rundownId,
    unsubscribers: [],
    unsubscribeRunner: null,
    runnerId: null,
    eventId: null,
    variableKeys: [],
    stopped: false,
    staleToast: null,
  }
  activeSession = session

  resetStore(sessionStore, initialState)
  sessionStore.rundownId = rundownId
  sessionStore.status = 'loading'

  columnShareLinkActions.set(readShareLinkColumns())

  void requestToken(session)
  subscribeRundown(session)
  subscribeCues(session)
  subscribeColumns(session)
  subscribeCells(session)
  subscribeMentions(session)
}

/**
 * Tear down the active session: unsubscribe everything and reset every store
 * the session wrote into, so the next rundown starts from a clean slate.
 */
export function stopRundownSession(): void {
  const session = activeSession
  if (!session) return

  session.stopped = true
  activeSession = null

  session.unsubscribers.forEach((unsubscribe) => unsubscribe())
  session.unsubscribeRunner?.()
  session.staleToast?.dismiss()

  clearVariables(session)
  queryClient.removeQueries({
    queryKey: mentionsQueryKeys.list({ rundownId: session.rundownId }).queryKey,
  })

  rundownStoreActions.resetStore()
  jotai.set(runnerAtom, null)
  jotai.set(planAtom, null)
  jotai.set(eventAtom, null)

  cellsStoreActions.resetStore()
  columnsStoreActions.resetStore()
  cuesStoreActions.resetStore()
  columnShareLinkActions.resetStore()
  cueUiActions.resetStore()
  cueDndActions.resetStore()
  resetCueOrderSave()
  pendingCueOrderActions.resetStore()
  subTimerRunsActions.resetStore()
  cueTimingActions.resetStore()

  RundownToken.reset()

  resetStore(sessionStore, initialState)
}

export function useRundownSession() {
  return useSnapshot(sessionStore)
}

/**
 * Read the session state outside React.
 *
 * Returns an immutable snapshot, so the proxy itself stays private to this
 * module and callers cannot mutate the session by accident.
 */
export function getRundownSessionSnapshot(): Snapshot<RundownSessionState> {
  return snapshot(sessionStore)
}

// =============== INTERNALS

/**
 * Column settings of the share link, read once: they are signed together with
 * the URL, so they cannot change without a new session.
 */
function readShareLinkColumns() {
  const { searchParams } = new URL(window.location.href)
  const list = (name: string) =>
    searchParams.get(name)?.split(',').filter(Boolean) ?? []

  return {
    hiddenColumns: list('hiddenColumns'),
    editableColumns: list('edit-columns'),
  }
}

function markLoaded(session: SessionHandle, key: keyof Loaded) {
  if (session.stopped) return
  sessionStore.loaded[key] = true

  if (sessionStore.status !== 'loading') return
  const allLoaded = Object.values(sessionStore.loaded).every(Boolean)
  if (allLoaded) sessionStore.status = 'ready'
}

function isSessionOver(session: SessionHandle) {
  return session.stopped || sessionStore.status === 'error'
}

function fail(session: SessionHandle, error: RundownSessionError) {
  if (isSessionOver(session)) return

  sessionStore.error = error
  sessionStore.status = 'error'
}

/**
 * Fail from a thrown value. Only unknown errors are reported to Sentry.
 */
function failFromThrown(session: SessionHandle, thrown: unknown) {
  if (isSessionOver(session)) return

  const error = toSessionError(thrown)

  console.error(thrown)

  if (error.reason === 'unknown') {
    captureException(error.cause)
  }

  fail(session, error)
}

/**
 * Convert whatever was thrown into a `RundownSessionError` here, at the
 * boundary, so the UI never has to know about axios.
 */
function toSessionError(error: unknown): RundownSessionError {
  if (isAxiosError(error)) {
    const status = error.response?.status ?? null

    // `create-token` refuses guests and non-members with 401.
    if (status === 401) return { reason: 'unauthorized' }
    if (status === 404) return { reason: 'not-found' }

    const { data } = error.response ?? {}
    return {
      reason: 'unknown',
      status,
      message: typeof data === 'string' && data ? data : error.message,
      cause: error, // `AxiosError` is an `Error`, so name and stacktrace are kept
    }
  }

  return {
    reason: 'unknown',
    status: null,
    message: getErrorMessage(error),
    // Anything can be thrown, but the debug log needs a real `Error`.
    cause: error instanceof Error ? error : new Error(getErrorMessage(error)),
  }
}

/**
 * Error handler for session listeners. Firestore ends a listener for good
 * when it errors, so this must never be silent.
 *
 * While loading, the session fails, otherwise the page would load forever.
 * Once ready, the page stays usable mid-show (see `reportListenerError`).
 */
function onListenerError(session: SessionHandle, listener: string) {
  return (error: FirestoreError) =>
    reportListenerError(session, listener, error)
}

/**
 * Report a listener error, or a snapshot that failed to parse. While loading
 * the session fails. Once ready the page keeps its last good data and only the
 * error is reported, with one toast per session so the operator knows the
 * page may be out of date.
 */
function reportListenerError(
  session: SessionHandle,
  listener: string,
  error: unknown,
) {
  if (session.stopped) return

  if (sessionStore.status === 'loading') {
    failFromThrown(session, error)
    return
  }

  console.error(`[rundown-session] ${listener} listener failed:`, error)
  Sentry.captureException(error, { tags: { listener, code: errorCode(error) } })

  if (session.staleToast) return
  session.staleToast = triggerToast({
    type: 'fail',
    title: 'Some live updates failed',
    body: 'This rundown may be out of date. Refresh the page.',
    duration: -1,
  })
}

function errorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('code' in error))
    return undefined
  return typeof error.code === 'string' ? error.code : undefined
}

/**
 * Token first, then plan. The plan needs the token header, and firing it here
 * (rather than letting the axios interceptor lazily fetch the token) keeps the
 * token request on the critical path where we can observe it.
 */
async function requestToken(session: SessionHandle) {
  const url = new URL(window.location.href)
  const signature = url.searchParams.get('signature')
  url.searchParams.delete('signature')
  RundownToken.setParameters(session.rundownId, url.href, signature)

  try {
    await RundownToken.getToken()
    markLoaded(session, 'token')
  } catch (error) {
    failFromThrown(session, error)
    return
  }

  try {
    const { data } = await rundownPlan(session.rundownId, { toast: false })
    if (session.stopped) return
    jotai.set(planAtom, planFromSerialized(data))
    markLoaded(session, 'plan')
  } catch (error) {
    // 403 from `/plan` === rundown is archived
    // Also handled in `subscribeToRundown`, so this shouldn't log
    if (isAxiosError(error) && error.response?.status === 403) {
      fail(session, { reason: 'archived' })
      return
    }

    failFromThrown(session, error)
  }
}

function subscribeRundown(session: SessionHandle) {
  const rundownRef = doc(db, RUNDOWN_COLLECTION, session.rundownId)

  session.unsubscribers.push(
    onSnapshot(
      rundownRef,
      (snapshot) => {
        if (session.stopped) return

        if (!snapshot.exists()) {
          clearVariables(session)
          fail(session, { reason: 'not-found' })
          return
        }

        let rundown: Rundown
        try {
          rundown = parseRundownSnapshot(snapshot)
        } catch (error) {
          reportListenerError(session, 'rundown', error)
          return
        }

        // Always check if rundown has been archived, to prevent further edits.
        if (rundown.archivedAt) {
          fail(session, { reason: 'archived' })
          return
        }

        // The listener is the only writer of the rundown.
        rundownStoreActions.processDoc(rundown)
        syncVariables(session, rundown)
        syncRunner(session, rundown.runnerId)
        syncEvent(session, rundown.eventId)

        markLoaded(session, 'rundown')
      },
      onListenerError(session, 'rundown'),
    ),
  )
}

function subscribeCues(session: SessionHandle) {
  const cuesQuery = query(
    collection(db, RUNDOWN_COLLECTION, session.rundownId, CUE_COLLECTION),
    where('deletedAt', '==', null),
  )

  session.unsubscribers.push(
    onSnapshot(
      cuesQuery,
      (snapshot: QuerySnapshot) => {
        if (session.stopped) return
        cuesStoreActions.processDocChanges(snapshot.docChanges())
        markLoaded(session, 'cues')
      },
      onListenerError(session, 'cues'),
    ),
  )
}

function subscribeColumns(session: SessionHandle) {
  const columnsQuery = query(
    collection(db, RUNDOWN_COLLECTION, session.rundownId, COLUMN_COLLECTION),
    where('deletedAt', '==', null),
  )

  session.unsubscribers.push(
    onSnapshot(
      columnsQuery,
      (snapshot: QuerySnapshot) => {
        if (session.stopped) return
        columnsStoreActions.processDocChanges(snapshot.docChanges())
        markLoaded(session, 'columns')
      },
      onListenerError(session, 'columns'),
    ),
  )
}

function subscribeCells(session: SessionHandle) {
  const cellsRef = collection(
    db,
    RUNDOWN_COLLECTION,
    session.rundownId,
    CELL_COLLECTION,
  )

  session.unsubscribers.push(
    onSnapshot(
      cellsRef,
      (snapshot: QuerySnapshot) => {
        if (session.stopped) return
        cellsStoreActions.processDocChanges(snapshot.docChanges())
        markLoaded(session, 'cells')
      },
      onListenerError(session, 'cells'),
    ),
  )
}

function subscribeMentions(session: SessionHandle) {
  const { rundownId } = session
  const mentionsRef = collection(
    db,
    RUNDOWN_COLLECTION,
    rundownId,
    MENTION_COLLECTION,
  )

  session.unsubscribers.push(
    onSnapshot(
      mentionsRef,
      (snapshot: QuerySnapshot) => {
        if (session.stopped) return

        queryClient.setQueryData(
          mentionsQueryKeys.list({ rundownId }).queryKey,
          snapshot.docs
            .map((mentionDoc) =>
              mentionFromSnapshot(
                mentionDoc as unknown as SharedDocumentSnapshot,
              ),
            )
            // `key` is a string holding a counter, so compare numerically.
            // A string sort would place "10" before "9".
            .sort((a, b) => parseInt(a.key, 10) - parseInt(b.key, 10)),
        )

        snapshot.docChanges().forEach((change) => {
          const mentionKey = mentionsQueryKeys.byMentionId({
            rundownId,
            mentionId: change.doc.id,
          }).queryKey

          if (change.type === 'removed') {
            queryClient.removeQueries({ queryKey: mentionKey })
          } else {
            queryClient.setQueryData(
              mentionKey,
              mentionFromSnapshot(
                change.doc as unknown as SharedDocumentSnapshot,
              ),
            )
          }
        })
      },
      onListenerError(session, 'mentions'),
    ),
  )
}

/**
 * The runner lives in its own collection and can be swapped on the rundown
 * (start/stop show). Resubscribe only when `runnerId` actually changes.
 */
function syncRunner(session: SessionHandle, runnerId: string | null) {
  if (runnerId === session.runnerId) return
  session.runnerId = runnerId

  session.unsubscribeRunner?.()
  session.unsubscribeRunner = null
  // Sub timer runs belong to a runner: a new runner starts with none
  subTimerRunsActions.resetStore()

  if (!runnerId) {
    jotai.set(runnerAtom, null)
    return
  }

  const unsubscribeRunnerDoc = onSnapshot(
    doc(db, RUNNER_COLLECTION, runnerId),
    (snapshot: DocumentSnapshot) => {
      if (session.stopped) return
      jotai.set(
        runnerAtom,
        parseSnapshot(
          snapshot as unknown as SharedDocumentSnapshot,
        ) as Runner | null,
      )
    },
    onListenerError(session, 'runner'),
  )
  // Its own subcollection, so sub timer actions never touch the runner doc
  // (and never re-render everything that reads it)
  const unsubscribeSubTimerRuns = onSnapshot(
    collection(db, RUNNER_COLLECTION, runnerId, SUB_TIMER_RUN_COLLECTION),
    (snapshot: QuerySnapshot) => {
      if (session.stopped) return
      subTimerRunsActions.processDocChanges(snapshot.docChanges())
    },
  )
  session.unsubscribeRunner = () => {
    unsubscribeRunnerDoc()
    unsubscribeSubTimerRuns()
  }
}

/**
 * The event is fetched over HTTP (Firestore rules deny reading `events`).
 * Only refetch when `eventId` changes, not on every rundown snapshot.
 */
function syncEvent(session: SessionHandle, eventId: string | null) {
  if (eventId === session.eventId) return
  session.eventId = eventId

  if (!eventId) {
    jotai.set(eventAtom, null)
    return
  }

  void getRundownEvent(session.rundownId, { toast: false })
    .then(({ data }) => {
      if (session.stopped || session.eventId !== eventId) return
      jotai.set(eventAtom, eventFromSerialized(data))
    })
    .catch((error: unknown) => {
      // The rundown renders fine without its event.
      captureException(error)
    })
}

/**
 * Mirror `rundown.textVariables` into React Query (list + per-key) and the
 * variables atom, dropping cache entries for keys that no longer exist.
 */
function syncVariables(session: SessionHandle, rundown: Rundown) {
  const { rundownId } = session
  const { textVariables } = rundown
  const nextKeys = Object.keys(textVariables)

  queryClient.setQueryData(
    variablesQueryKeys.list({ rundownId }).queryKey,
    textVariables,
  )

  for (const key of session.variableKeys) {
    if (!nextKeys.includes(key)) {
      queryClient.removeQueries({
        queryKey: variablesQueryKeys.byVariableKey({
          rundownId,
          variableKey: key,
        }).queryKey,
      })
    }
  }

  for (const key of nextKeys) {
    queryClient.setQueryData(
      variablesQueryKeys.byVariableKey({ rundownId, variableKey: key })
        .queryKey,
      {
        key,
        value: textVariables[key],
      },
    )
  }

  session.variableKeys = nextKeys
  jotai.set(variablesAtom, textVariables)
}

function clearVariables(session: SessionHandle) {
  const { rundownId } = session

  for (const key of session.variableKeys) {
    queryClient.removeQueries({
      queryKey: variablesQueryKeys.byVariableKey({
        rundownId,
        variableKey: key,
      }).queryKey,
    })
  }
  queryClient.removeQueries({
    queryKey: variablesQueryKeys.list({ rundownId }).queryKey,
  })

  session.variableKeys = []
  jotai.set(variablesAtom, {})
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(sessionStore, { name: 'rundownSessionStore', enabled: true })
}
// #endregion
